<template>
    <!-- MODE COMPACT — les mêmes réglages, posés directement sur la ligne de l'article au lieu
         d'occuper un écran d'étape : formule, nombre de personnes, options du type. Aucune règle
         ne change ici, ce gabarit consomme les MÊMES computeds (`durationOptions`, `selectOptions`,
         `ticketActivities`) et les mêmes émissions que le formulaire complet ci-dessous. -->
    <div v-if="compact" class="participants-compact" @click.stop>
        <div v-if="(isPaintball || isVr || isBooking) && itemDetails?.durations?.length > 0" class="pc-field pc-field--select">
            <Clock :size="13" class="pc-icon" />
            <base-select
                :value="durationId"
                @input="$emit('update:durationId', $event)"
                :options="durationOptions"
                placeholder="Choix de la formule"
                fixed-dropdown
                customClass="pc-select"
            />
        </div>

        <!-- Compteur plutôt que liste déroulante : le geste courant est « une personne de plus ».
             Il ne parcourt QUE les valeurs autorisées par le catalogue (`participantValues`), donc
             il ne peut pas produire un nombre que le sélecteur complet aurait refusé. -->
        <div v-if="!lockSpace && !isSimpleTicket" class="pc-field pc-counter">
            <Users :size="13" class="pc-icon" />
            <button
                type="button"
                :disabled="!canDecrementParticipants"
                :aria-label="$t('devis.v3.participants_less')"
                @click.stop="stepParticipants(-1)"
            >
                <Minus :size="13" />
            </button>
            <span class="pc-count poppinsSemiBold">{{ value || 0 }}</span>
            <button
                type="button"
                :disabled="!canIncrementParticipants"
                :aria-label="$t('devis.v3.participants_more')"
                @click.stop="stepParticipants(1)"
            >
                <Plus :size="13" />
            </button>
        </div>

        <label v-if="isBooking && !isPaintball" class="pc-toggle">
            <input type="checkbox" v-model="localLockSpace" />
            <span>{{ $t('devis.v3.participants_whole_space') }}</span>
        </label>

        <label v-if="usesTimeSlots && isPaintball && itemDetails?.authorise_mixed && !lockSpace" class="pc-toggle">
            <input type="checkbox" v-model="localMixing" />
            <span>{{ $t('devis.v3.participants_shared_slot') }}</span>
        </label>

        <div v-if="isSimpleTicket" class="pc-tickets">
            <div v-for="activity in ticketActivities" :key="activity.id" class="pc-ticket">
                <span class="pc-ticket-name">{{ activity.name }}</span>
                <base-select
                    :value="ticketSelections[activity.id] || 0"
                    @input="handleTicketChange(activity.id, parseInt($event))"
                    :options="getTicketQuantityOptions(activity)"
                    placeholder="0"
                    fixed-dropdown
                    customClass="pc-select pc-select--tiny"
                />
            </div>
        </div>

        <info-banner
            v-if="!usesTimeSlots && outsideRecommendedRange"
            variant="warning"
            class="pc-warning"
            :text="$t('devis.v3.participants_outside_recommended_range', { min: recommendedMin, max: recommendedMax })"
        />
    </div>

    <div v-else class="step-participants-config">
        <div class="selection-box-v3">
            <div class="item-summary-v3" v-if="itemDetails">
                <span class="poppinsMedium ppv2-text-black" :style="{ fontSize: normalizeFontSize(15) }">{{
                    itemDetails.name
                }}</span>
                <span
                    class="poppinsMedium ppv2-text-sm ppv2-text-black ppv2-bg-black ppv2-bg-opacity-10 ppv2-rounded-lg ppv2-px-3 ppv2-py-1 ppv2-gap-2"
                >
                    <Users :size="16" />
                    {{ value }}
                </span>
            </div>

            <div class="booking-toggle-v3" v-if="isBooking && !isPaintball">
                <base-checkbox v-model="localLockSpace" label="Je souhaite réserver tout l'espace" />
            </div>

            <div class="form-group-v3 ppv2-mb-3" v-if="(isPaintball || isVr || isBooking) && itemDetails?.durations?.length > 0">
                <base-select
                    label="Choix de la formule"
                    required
                    custom-label-class="poppinsLight ppv2-text-sm ppv2-text-black"
                    :value="durationId"
                    @input="$emit('update:durationId', $event)"
                    :options="durationOptions"
                    placeholder="Choisir une formule"
                    fixed-dropdown
                />
            </div>

            <!-- Le mixage se persiste sur le créneau (time_slots.authorize_mixage). En mode sans
                 créneau aucun n'est créé : la case n'aurait aucun effet, on ne la propose pas. -->
            <div
                class="booking-toggle-v3 ppv2-mb-3"
                v-if="usesTimeSlots && isPaintball && itemDetails?.authorise_mixed && !lockSpace"
            >
                <base-checkbox
                    v-model="localMixing"
                    label="Permettre à d'autres participants de rejoindre mon créneau"
                    has-border-checked
                />
            </div>

            <!-- SimpleTicket List -->
            <div v-if="isSimpleTicket" class="simple-ticket-list">
                <div class="poppinsLight ppv2-text-sm ppv2-text-black mb-4">Sélectionnez le nombre d'entrées</div>
                <div
                    v-for="activity in ticketActivities"
                    :key="activity.id"
                    class="ticket-card-v3 ppv2-border ppv2-border-solid ppb2-border-black ppv2-border-opacity-50 hover:ppv2-border-opacity-70"
                >
                    <div class="ticket-card-content">
                        <div class="ticket-image-placeholder">
                            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                                <rect x="3" y="3" width="18" height="18" rx="2" ry="2" />
                                <circle cx="8.5" cy="8.5" r="1.5" />
                                <polyline points="21 15 16 10 5 21" />
                            </svg>
                        </div>
                        <div class="ticket-info">
                            <div class="ticket-name">{{ activity.name }} • {{ activity.price }}€</div>
                        </div>
                    </div>
                    <div class="ticket-quantity">
                        <base-select
                            :value="ticketSelections[activity.id] || 0"
                            @input="handleTicketChange(activity.id, parseInt($event))"
                            :options="getTicketQuantityOptions(activity)"
                            placeholder="Choisir"
                            customClass="ticket-qty-select"
                            fixed-dropdown
                        />
                    </div>
                </div>
            </div>

            <div class="form-group-v3 ppv2-mb-3" v-if="!lockSpace && !isSimpleTicket">
                <!-- Mode AVEC créneau : le nombre de personnes est borné par le catalogue, car il
                     sera confronté à la capacité réelle du créneau à la réservation. -->
                <base-select
                    v-if="usesTimeSlots"
                    label="Nombre de personnes"
                    required
                    custom-label-class="poppinsLight ppv2-text-sm ppv2-text-black"
                    :value="value"
                    @input="$emit('input', parseInt($event))"
                    :options="selectOptions"
                    placeholder="Choisir le nombre de personnes"
                    fixed-dropdown
                />

                <!-- Mode SANS créneau : aucune réservation n'est posée, aucune capacité n'est
                     consommée. La saisie est donc libre ; on se contente d'alerter quand elle
                     sort de la plage praticable de l'espace. -->
                <template v-else>
                    <!-- digitsOnly : CustomInput filtre la saisie caractère par caractère et
                         réécrit la valeur du champ. Un `type=number` seul laisse passer e, +, -, .
                         et le collage de texte. -->
                    <custom-input
                        label="Nombre de personnes"
                        required
                        type="number"
                        min="1"
                        digits-only
                        :value="value"
                        placeholder="Saisir le nombre de personnes"
                        customLabelClass="poppinsLight ppv2-text-sm ppv2-text-black"
                        classContainerAdd="!ppv2-mb-0 !ppv2-opacity-100"
                        @input="onFreeParticipantsInput"
                    />
                    <info-banner
                        v-if="outsideRecommendedRange"
                        variant="warning"
                        class="ppv2-mt-2"
                        :text="
                            $t('devis.v3.participants_outside_recommended_range', { min: recommendedMin, max: recommendedMax })
                        "
                    />
                </template>
            </div>
        </div>
    </div>
</template>

<script>
import BaseSelect from '../../../Shared/BaseSelect.vue'
import CustomInput from '../../../Inputs/CustomInput.vue'
import InfoBanner from '../../../Shared/InfoBanner.vue'
import BaseCheckbox from '../../../Shared/BaseCheckbox.vue'
import normalizeFontSize from '../../../../utils/normalizeFontSize.js'
import { formatDuration } from '../../../../utils/duration.js'
import { Users, Clock, Plus, Minus } from 'lucide-vue'
export default {
    name: 'StepParticipants',
    components: {
        BaseSelect,
        CustomInput,
        InfoBanner,
        BaseCheckbox,
        Users,
        Clock,
        Plus,
        Minus,
    },
    props: {
        value: { type: Number, default: 1 },
        itemDetails: { type: Object, default: null },
        mixing: { type: Boolean, default: false },
        durationId: { type: [Number, String], default: null },
        ticketSelections: { type: Object, default: () => ({}) },
        // Mode de composition du devis. Défaut `true` = comportement historique inchangé.
        usesTimeSlots: { type: Boolean, default: true },
        // Privatisation de l'espace et drapeau "valeurs par défaut déjà appliquées" : portés par
        // le parent car cette étape est DÉTRUITE en passant à l'étape suivante (v-if) et recréée
        // au retour. En data locale, ils repartaient à zéro et écrasaient les choix de l'utilisateur.
        lockSpace: { type: Boolean, default: false },
        initialised: { type: Boolean, default: false },
        // Gabarit COMPACT, posé sur la ligne de l'article. Ne change que la mise en forme :
        // les mêmes réglages, les mêmes bornes et les mêmes émissions.
        compact: { type: Boolean, default: false },
    },
    computed: {
        localLockSpace: {
            get() {
                return this.lockSpace
            },
            set(val) {
                this.$emit('update:lockSpace', val)
            },
        },
        localMixing: {
            get() {
                return this.mixing
            },
            set(val) {
                this.$emit('update:mixing', val)
            },
        },
        // Bornes réellement proposées par le catalogue, identiques à celles du select du mode
        // avec créneau. `possible_tickets` fait autorité quand la prestation définit une liste
        // de valeurs autorisées (list_numbers_tickes) : elle démarre alors au-dessus de 1 et est
        // déjà plafonnée à min(max_contributors, capacité des espaces) côté serveur
        // (TimeSlotService::getPossiblePlaces). Sans liste, on retombe sur min/max_contributors.
        recommendedMin() {
            const allowed = (this.itemDetails?.possible_tickets || []).filter((n) => n >= this.min)

            return allowed.length > 0 ? Math.min(...allowed) : this.min
        },
        recommendedMax() {
            const tickets = this.itemDetails?.possible_tickets || []

            return tickets.length > 0 ? Math.max(...tickets) : this.max
        },
        // Valeur hors de la plage réellement praticable, au-dessus comme en dessous. Purement
        // informatif : en mode sans créneau, rien n'est réservé et rien ne bloque la validation.
        // Un champ vide (NaN) n'est pas une valeur hors plage : on n'alerte pas dessus.
        outsideRecommendedRange() {
            if (this.usesTimeSlots) {
                return false
            }

            const value = Number(this.value)

            return Number.isFinite(value) && (value < this.recommendedMin || value > this.recommendedMax)
        },
        isPaintball() {
            return this.itemDetails?.subcategory_slug === 'paintball'
        },
        isBooking() {
            return this.itemDetails?.subcategory_slug === 'booking'
        },
        isVr() {
            return this.itemDetails?.subcategory_slug === 'vr'
        },
        isSimpleTicket() {
            return this.itemDetails?.subcategory_slug === 'simpleTicket'
        },
        ticketActivities() {
            return this.itemDetails?.list_activites || []
        },
        getTicketQuantityOptions() {
            return (activity) => {
                const options = [{ value: 0, label: 'Aucun billet sélectionné' }]
                let values = []

                if (activity.possible_tickets && activity.possible_tickets.length > 0) {
                    values = activity.possible_tickets
                } else {
                    for (let i = 1; i <= 20; i++) {
                        values.push(i)
                    }
                }

                values.forEach((v) => {
                    options.push({ value: v, label: `${v} billet${v > 1 ? 's' : ''}` })
                })
                return options
            }
        },
        min() {
            return this.itemDetails?.min_contributors || 1
        },
        max() {
            return this.itemDetails?.max_contributors || 99
        },
        durationOptions() {
            const itemName = (this.itemDetails?.name || '').trim()
            return (this.itemDetails?.durations || []).map((d) => {
                const minutes = formatDuration(d.duration, this.$i18n || 'fr')
                const desc = (d.price_duration_description || '').trim()
                // Côté back, faute de nom personnalisé, `price_duration_description` reçoit le nom
                // de l'expérience : on ne l'affiche que s'il s'agit d'un vrai libellé de formule.
                const hasCustomDesc = desc && desc !== itemName
                let label
                if (hasCustomDesc) {
                    label = minutes ? `${desc} (${minutes})` : desc
                } else {
                    label = minutes || desc || 'Formule'
                }
                return { value: d.id, label }
            })
        },
        /**
         * Valeurs de participants autorisées, dans l'ordre. C'est EXACTEMENT la liste du
         * sélecteur complet : le compteur compact ne peut donc pas produire un nombre que
         * le catalogue refuse.
         */
        participantValues() {
            return this.selectOptions.map((option) => option.value)
        },
        canDecrementParticipants() {
            // Mode sans créneau : saisie libre, seule la borne à 1 s'applique.
            if (!this.usesTimeSlots) return (parseInt(this.value, 10) || 0) > 1

            return this.participantValues.indexOf(this.value) > 0
        },
        canIncrementParticipants() {
            if (!this.usesTimeSlots) return true

            const index = this.participantValues.indexOf(this.value)

            return index >= 0 && index < this.participantValues.length - 1
        },
        selectOptions() {
            let values = []
            if (this.itemDetails?.possible_tickets && this.itemDetails.possible_tickets.length > 0) {
                values = this.itemDetails.possible_tickets
            } else {
                // Generate range from min to max
                for (let i = this.min; i <= Math.min(this.max, 50); i++) {
                    values.push(i)
                }
            }

            // `possible_tickets` démarre toujours à 1 côté back (getPossiblePlaces) : on retire
            // ici les valeurs sous le minimum de participants de la prestation (min_contributors).
            values = values.filter((n) => n >= this.min)

            return values.map((n) => ({
                value: n,
                label:
                    n === this.max && (this.isBooking || this.isPaintball)
                        ? `${n} personnes (Tout l'espace)`
                        : `${n} ${n > 1 ? 'personnes' : 'personne'}`,
            }))
        },
    },
    methods: {
        /**
         * Avance ou recule d'un cran dans les valeurs autorisées.
         *
         * Si la valeur courante n'est pas dans la liste — un devis composé avant un changement
         * de catalogue, par exemple — on se raccroche à la première valeur atteignable plutôt
         * que de bloquer le compteur.
         */
        stepParticipants(delta) {
            if (!this.usesTimeSlots) {
                this.$emit('input', Math.max(1, (parseInt(this.value, 10) || 1) + delta))
                return
            }

            const values = this.participantValues
            if (values.length === 0) return

            let index = values.indexOf(this.value)
            if (index < 0) {
                const nearest = values.findIndex((v) => v >= (parseInt(this.value, 10) || 0))
                index = nearest < 0 ? values.length - 1 : nearest
            }

            const next = values[Math.min(values.length - 1, Math.max(0, index + delta))]
            if (next !== undefined) this.$emit('input', next)
        },
        onFreeParticipantsInput(raw) {
            const parsed = parseInt(raw)
            this.$emit('input', isNaN(parsed) || parsed < 1 ? 1 : parsed)
        },
        normalizeFontSize,
        handleTicketChange(activityId, quantity) {
            const newSelections = { ...this.ticketSelections, [activityId]: quantity }
            this.$emit('update:ticketSelections', newSelections)

            // Calculate total pax as sum of all tickets
            const totalPax = Object.values(newSelections).reduce((sum, q) => sum + q, 0)
            this.$emit('input', totalPax)
        },
    },
    watch: {
        value(val) {
            // Logic: si on a choisi le max en le decoche par default
            if (val === this.max) {
                this.localMixing = false
            }
        },
        localMixing(val) {
            // Logic: si on le coche donc on le coche le minimum de nombre de personne
            if (val) {
                let safeMin = parseInt(this.min)
                if (this.itemDetails?.possible_tickets?.length > 0 && !this.itemDetails.possible_tickets.includes(safeMin)) {
                    safeMin = this.itemDetails.possible_tickets[0]
                }
                this.$emit('input', safeMin)
            }
        },
        lockSpace(val) {
            if (val) {
                this.$emit('input', this.max)
                this.localMixing = false
            } else if (this.isBooking) {
                // Un booking n'a pas de case de mixage dédiée (elle n'existe que pour le
                // paintball) : ne PAS privatiser l'espace vaut acceptation que d'autres
                // participants rejoignent le créneau.
                this.localMixing = true
            }
        },
        itemDetails: {
            immediate: true,
            handler(newVal) {
                if (!newVal) return

                // Valeurs par défaut appliquées UNE SEULE FOIS par prestation. Ce watcher est
                // `immediate` et l'étape est recréée à chaque retour arrière : sans ce garde-fou,
                // revenir de l'étape 3 recochait la privatisation et remettait le nombre de
                // personnes au maximum, effaçant la saisie.
                if (this.initialised) return
                this.$emit('update:initialised', true)

                if (this.isBooking) {
                    this.localLockSpace = true
                    this.$emit('input', this.max)
                } else if (!this.isSimpleTicket && this.value < this.min) {
                    // La prestation peut exiger un minimum > 1 : on cale la valeur initiale
                    // sur ce minimum pour qu'elle existe bien dans les options du select.
                    this.$emit('input', this.min)
                }
            },
        },
    },
}
</script>

<style scoped>
.step-participants-config {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    flex: 1;
    min-height: 0;
    width: 100%;
    padding: 20px;
    margin-bottom: 30px;
}

.selection-box-v3 {
    border-radius: 16px;
    padding: 30px;
    max-width: 600px;
    width: 100%;
}

.item-summary-v3 {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 12px;
    margin-bottom: 24px;
}

.booking-toggle-v3 {
    margin-bottom: 20px;
    display: flex;
    justify-content: flex-start;
}

.form-group-v3 {
    text-align: left;
}

/* SimpleTicket Styles */
.simple-ticket-list {
    margin-top: 10px;
}

.ticket-card-v3 {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 12px 20px;
    border-radius: 12px;
    margin-bottom: 12px;
    transition: all 0.2s;
}

.ticket-card-v3:hover {
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.05);
}

.ticket-card-content {
    display: flex;
    align-items: center;
    gap: 16px;
}

.ticket-image-placeholder {
    width: 48px;
    height: 48px;
    border-radius: 8px;
    display: flex;
    align-items: center;
    justify-content: center;
    color: #94a3b8;
}

.ticket-name {
    font-weight: 600;
    font-size: 0.95rem;
}

.ticket-quantity {
    width: 200px;
}

/* ------------------------------------------------------------------ *
 *  Mode COMPACT — les réglages posés sur la ligne de l'article
 * ------------------------------------------------------------------ */

.participants-compact {
    display: flex;
    align-items: center;
    flex-wrap: wrap;
    gap: 8px;
    margin-top: 4px;
    cursor: default;
}

.pc-field {
    display: flex;
    align-items: center;
    gap: 6px;
    padding: 2px 8px;
    border: 1px solid #dfe4ec;
    border-radius: 9999px;
    background: #fff;
}

.pc-field--select {
    padding-right: 2px;
}

.pc-icon {
    flex: 0 0 auto;
    color: #7d2dbd;
}

/* Le sélecteur perd sa bordure : c'est la pill qui la porte, sinon on verrait deux cadres
   imbriqués sur une ligne déjà dense. */
.pc-select {
    min-width: 148px;
}

.pc-select--tiny {
    min-width: 74px;
}

.participants-compact >>> .pc-select .custom-select,
.participants-compact >>> .pc-select input,
.participants-compact >>> .pc-select .select-trigger {
    border: 0 !important;
    background: transparent !important;
    min-height: 26px !important;
    padding-top: 0 !important;
    padding-bottom: 0 !important;
    font-size: 12.5px !important;
}

.pc-counter button {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 22px;
    height: 22px;
    border: 0;
    border-radius: 50%;
    background: transparent;
    color: #475569;
    cursor: pointer;
    transition:
        background 0.18s ease,
        color 0.18s ease;
}

.pc-counter button:hover:not(:disabled) {
    background: rgba(125, 45, 189, 0.1);
    color: #7d2dbd;
}

.pc-counter button:disabled {
    opacity: 0.35;
    cursor: not-allowed;
}

.pc-count {
    min-width: 20px;
    text-align: center;
    color: #1d1d1b;
    font-size: 12.5px;
    font-variant-numeric: tabular-nums;
}

.pc-toggle {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    margin: 0;
    padding: 3px 10px;
    border: 1px solid #dfe4ec;
    border-radius: 9999px;
    background: #fff;
    color: #475569;
    font-family: 'Poppins', sans-serif;
    font-size: 12px;
    cursor: pointer;
}

.pc-toggle input {
    accent-color: #7d2dbd;
    cursor: pointer;
}

.pc-tickets {
    display: flex;
    align-items: center;
    flex-wrap: wrap;
    gap: 8px;
}

.pc-ticket {
    display: flex;
    align-items: center;
    gap: 6px;
    padding: 2px 4px 2px 10px;
    border: 1px solid #dfe4ec;
    border-radius: 9999px;
    background: #fff;
}

.pc-ticket-name {
    color: #475569;
    font-family: 'Poppins', sans-serif;
    font-size: 12px;
    white-space: nowrap;
}

.pc-warning {
    flex: 1 1 100%;
}
</style>
